import type { MapDocument } from "../document/types.ts";
import { CANVAS_COLOURS, type CanvasColour } from "./canvas-colours.ts";

/**
 * The Activation overlay's categories (ADR-070's amendment of 2026-09-27, P1.6-T49; the Overlay colours card of
 * the design's `1790408874-42c2`): eight fixed styles, each a colour, a pattern and a number, and the rule that
 * numbers behaviour types. A type's number is its place in the game's registration, so it keeps its colour on
 * every map and categories 7 and 8 appear only when the game registers more than six types; past eight,
 * numbers keep counting and the styles start again from the first. The patterns are the card's
 * 6-unit tile, anchored at the world's origin, given as rectangles and convex polygons clipped to a rectangle
 * so a renderer draws them with plain fills.
 */

/**
 * A category's pattern, the card's names: solid, diagonal hatch (/), dots, horizontal lines, cross-hatch,
 * back-diagonal hatch (\), vertical lines, checker.
 */
export type ActivationPattern = "solid" | "diagonal" | "dots" | "horizontal" | "cross" | "back-diagonal" | "vertical" | "checker";

/**
 * One category's style: its number among the eight, its colour and its pattern.
 */
export interface ActivationCategoryStyle {
  readonly category: number;
  readonly colour: CanvasColour;
  readonly pattern: ActivationPattern;
}

/**
 * A behaviour type as a map numbers it: its id, the label the legend shows, its number on the map and the style
 * that number draws in.
 */
export interface NumberedActivationType {
  readonly type: string;
  readonly label: string;
  readonly number: number;
  readonly style: ActivationCategoryStyle;
}

/**
 * The eight categories, in number order.
 */
export const ACTIVATION_CATEGORIES: readonly ActivationCategoryStyle[] = [
  { category: 1, colour: CANVAS_COLOURS.activationCategory1, pattern: "solid" },
  { category: 2, colour: CANVAS_COLOURS.activationCategory2, pattern: "diagonal" },
  { category: 3, colour: CANVAS_COLOURS.activationCategory3, pattern: "dots" },
  { category: 4, colour: CANVAS_COLOURS.activationCategory4, pattern: "horizontal" },
  { category: 5, colour: CANVAS_COLOURS.activationCategory5, pattern: "cross" },
  { category: 6, colour: CANVAS_COLOURS.activationCategory6, pattern: "back-diagonal" },
  { category: 7, colour: CANVAS_COLOURS.activationCategory7, pattern: "vertical" },
  { category: 8, colour: CANVAS_COLOURS.activationCategory8, pattern: "checker" },
];

/**
 * The pattern tile's side in design units.
 */
export const PATTERN_TILE_UNITS = 6;

const SOLID_ALPHA = 0.25;
const CHECKER_ALPHA = 0.7;
const LINE_UNITS = 1.5;
const CROSS_UNITS = 1.1;
const DOT_UNITS = 2.4;
const HALF_TILE = PATTERN_TILE_UNITS / 2;
const POLYGON_MAX = 8;
const clipIn = new Float64Array(POLYGON_MAX * 2);
const clipOut = new Float64Array(POLYGON_MAX * 2);

/**
 * The style a type's number on a map draws in: numbers 1 to 8 their own category, and past eight the categories
 * again from the first.
 *
 * @param number The type's number, a positive integer.
 * @returns The style.
 * @throws RangeError when the number is not a positive integer.
 */
export function activationCategoryStyle(number: number): ActivationCategoryStyle {
  if (!Number.isInteger(number) || number < 1) {
    throw new RangeError(`activation type number ${number} is not a positive integer`);
  }
  return ACTIVATION_CATEGORIES[(number - 1) % ACTIVATION_CATEGORIES.length] as ActivationCategoryStyle;
}

/**
 * The behaviour types a map's props carry, on every prop layer whether shown or hidden, so hiding a layer never
 * renumbers the map's types.
 *
 * @param map The map.
 * @returns The type ids.
 */
export function typesCarried(map: MapDocument): Set<string> {
  const types = new Set<string>();
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (prop.behaviour !== undefined) {
        types.add(prop.behaviour.type);
      }
    }
  }
  return types;
}

/**
 * Numbers the behaviour types a map carries by the game's registration: a registered type's number is its place
 * in the registration, 1 up, the same on every map and in every session; a type the game does not register
 * continues after the registered count, sorted by id and named by it. Only the carried types are listed, in
 * number order, so a map's numbers may have gaps.
 *
 * @param registered The game's registered types in registration order, each its id and label.
 * @param carried The types the map's props carry.
 * @returns The carried types, numbered, in number order.
 * @example numberActivationTypes([{ type: "sign", label: "Sign" }, { type: "gate", label: "Gate" }], ["gate"]) numbers Gate 2.
 */
export function numberActivationTypes(registered: readonly { readonly type: string; readonly label: string }[], carried: Iterable<string>): NumberedActivationType[] {
  const left = new Set(carried);
  const numbered: NumberedActivationType[] = [];
  registered.forEach((definition, i) => {
    if (left.delete(definition.type)) {
      numbered.push({ type: definition.type, label: definition.label, number: i + 1, style: activationCategoryStyle(i + 1) });
    }
  });
  const unregistered = [...left].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  unregistered.forEach((type, i) => {
    const number = registered.length + i + 1;
    numbered.push({ type, label: type, number, style: activationCategoryStyle(number) });
  });
  return numbered;
}

/**
 * Visits a rectangle's intersection with another, when it has an area.
 *
 * @param x0 The mark's left.
 * @param y0 The mark's top.
 * @param x1 The mark's right.
 * @param y1 The mark's bottom.
 * @param rx The clip's left.
 * @param ry The clip's top.
 * @param rx1 The clip's right.
 * @param ry1 The clip's bottom.
 * @param alpha The mark's opacity.
 * @param rect Called with the clipped rectangle.
 */
function clippedRect(x0: number, y0: number, x1: number, y1: number, rx: number, ry: number, rx1: number, ry1: number, alpha: number, rect: PatternRectVisitor): void {
  const left = Math.max(x0, rx);
  const top = Math.max(y0, ry);
  const right = Math.min(x1, rx1);
  const bottom = Math.min(y1, ry1);
  if (right > left && bottom > top) {
    rect(left, top, right - left, bottom - top, alpha);
  }
}

/**
 * Clips a convex polygon to the half-plane `a x + b y + d >= 0` (Sutherland and Hodgman).
 *
 * @param source The polygon's points, x and y interleaved.
 * @param count Its number of points.
 * @param out Where the clipped points are written.
 * @param a The half-plane's x coefficient.
 * @param b Its y coefficient.
 * @param d Its constant.
 * @returns The clipped polygon's number of points.
 */
function clipHalfPlane(source: Float64Array, count: number, out: Float64Array, a: number, b: number, d: number): number {
  let written = 0;
  for (let i = 0; i < count; i += 1) {
    const j = (i + 1) % count;
    const px = source[2 * i] as number;
    const py = source[2 * i + 1] as number;
    const qx = source[2 * j] as number;
    const qy = source[2 * j + 1] as number;
    const p = a * px + b * py + d;
    const q = a * qx + b * qy + d;
    if (p >= 0) {
      out[2 * written] = px;
      out[2 * written + 1] = py;
      written += 1;
    }
    if ((p >= 0) !== (q >= 0)) {
      const t = p / (p - q);
      out[2 * written] = px + (qx - px) * t;
      out[2 * written + 1] = py + (qy - py) * t;
      written += 1;
    }
  }
  return written;
}

/**
 * Visits the bands of a diagonal hatch clipped to a rectangle: lines `a x + b y = k T` for each tile side `T`, `b`
 * being 1 for the diagonal (/) and -1 for the back-diagonal (\).
 *
 * @param b The lines' y coefficient.
 * @param width The bands' width in world pixels, across the line.
 * @param tile The tile's side in world pixels.
 * @param x The rectangle's left.
 * @param y The rectangle's top.
 * @param w Its width.
 * @param h Its height.
 * @param polygon Called with each band's polygon.
 */
function diagonalBands(b: number, width: number, tile: number, x: number, y: number, w: number, h: number, polygon: PatternPolygonVisitor): void {
  const half = (width / 2) * Math.SQRT2;
  const low = b > 0 ? x + y : x - (y + h);
  const high = b > 0 ? x + w + y + h : x + w - y;
  const first = Math.ceil((low - half) / tile);
  const last = Math.floor((high + half) / tile);
  for (let k = first; k <= last; k += 1) {
    const c = k * tile;
    clipIn[0] = x;
    clipIn[1] = y;
    clipIn[2] = x + w;
    clipIn[3] = y;
    clipIn[4] = x + w;
    clipIn[5] = y + h;
    clipIn[6] = x;
    clipIn[7] = y + h;
    const kept = clipHalfPlane(clipIn, 4, clipOut, 1, b, -(c - half));
    const count = clipHalfPlane(clipOut, kept, clipIn, -1, -b, c + half);
    if (count >= 3) {
      polygon(clipIn, count, 1);
    }
  }
}

/**
 * Receives one rectangle of a pattern: its left, top, width and height in world pixels and its opacity, a share
 * of the category colour's own.
 */
export type PatternRectVisitor = (x: number, y: number, w: number, h: number, alpha: number) => void;

/**
 * Receives one convex polygon of a pattern: a buffer whose first `count` points, x and y interleaved, are the
 * polygon, and its opacity. The buffer is reused by the next call, so a caller copies what it keeps.
 */
export type PatternPolygonVisitor = (points: Float64Array, count: number, alpha: number) => void;

/**
 * Visits a pattern's marks over a rectangle of the world, clipped to it, the tile anchored at the world's origin
 * so neighbouring rectangles continue one another's pattern.
 *
 * @param pattern The pattern.
 * @param x The rectangle's left in world pixels.
 * @param y Its top.
 * @param w Its width.
 * @param h Its height.
 * @param unit World pixels a design unit: the screen's pixels a unit over the camera's zoom.
 * @param rect Called with each rectangular mark.
 * @param polygon Called with each polygonal mark.
 * @throws RangeError when the unit is not a positive finite number.
 */
export function forEachPatternMark(pattern: ActivationPattern, x: number, y: number, w: number, h: number, unit: number, rect: PatternRectVisitor, polygon: PatternPolygonVisitor): void {
  if (!(unit > 0 && Number.isFinite(unit))) {
    throw new RangeError(`pattern unit ${unit} is not a positive number`);
  }
  if (!(w > 0 && h > 0)) {
    return;
  }
  const tile = PATTERN_TILE_UNITS * unit;
  const x1 = x + w;
  const y1 = y + h;
  const firstColumn = Math.floor(x / tile);
  const lastColumn = Math.floor(x1 / tile);
  const firstRow = Math.floor(y / tile);
  const lastRow = Math.floor(y1 / tile);
  switch (pattern) {
    case "solid":
      rect(x, y, w, h, SOLID_ALPHA);
      return;
    case "horizontal": {
      const band = (LINE_UNITS / 2) * unit;
      for (let j = firstRow; j <= lastRow; j += 1) {
        const middle = j * tile + HALF_TILE * unit;
        clippedRect(x, middle - band, x1, middle + band, x, y, x1, y1, 1, rect);
      }
      return;
    }
    case "vertical": {
      const band = (LINE_UNITS / 2) * unit;
      for (let i = firstColumn; i <= lastColumn; i += 1) {
        const middle = i * tile + HALF_TILE * unit;
        clippedRect(middle - band, y, middle + band, y1, x, y, x1, y1, 1, rect);
      }
      return;
    }
    case "dots": {
      const half = (DOT_UNITS / 2) * unit;
      for (let j = firstRow; j <= lastRow; j += 1) {
        for (let i = firstColumn; i <= lastColumn; i += 1) {
          const cx = i * tile + HALF_TILE * unit;
          const cy = j * tile + HALF_TILE * unit;
          clippedRect(cx - half, cy - half, cx + half, cy + half, x, y, x1, y1, 1, rect);
        }
      }
      return;
    }
    case "checker": {
      const side = HALF_TILE * unit;
      for (let j = firstRow; j <= lastRow; j += 1) {
        for (let i = firstColumn; i <= lastColumn; i += 1) {
          const left = i * tile;
          const top = j * tile;
          clippedRect(left, top, left + side, top + side, x, y, x1, y1, CHECKER_ALPHA, rect);
          clippedRect(left + side, top + side, left + tile, top + tile, x, y, x1, y1, CHECKER_ALPHA, rect);
        }
      }
      return;
    }
    case "diagonal":
      diagonalBands(1, LINE_UNITS * unit, tile, x, y, w, h, polygon);
      return;
    case "back-diagonal":
      diagonalBands(-1, LINE_UNITS * unit, tile, x, y, w, h, polygon);
      return;
    case "cross":
      diagonalBands(1, CROSS_UNITS * unit, tile, x, y, w, h, polygon);
      diagonalBands(-1, CROSS_UNITS * unit, tile, x, y, w, h, polygon);
      return;
  }
}
